/**
 * Excel read/write: selection values, write table values, write formula.
 * All Excel.run and range access lives here for testability and reuse.
 */

export type ExcelRunContext = {
	workbook: {
		getSelectedRange: () => {
			load?: (props: string) => void
			values?: (string | number)[][]
			formulas?: string[][]
			getResizedRange?: (deltaRows: number, deltaColumns: number) => {
				values: (string | number)[][]
			}
		}
	}
	sync: () => Promise<void>
}

export type ExcelApi = {
	run: (callback: (context: ExcelRunContext) => Promise<void>) => Promise<void>
}

/**
 * Returns the Excel API (window.Excel) or null if not in Excel host.
 */
export function getExcelApi(): ExcelApi | null {
	const win = typeof window !== "undefined" ? window : (globalThis as unknown as Window)
	return (win as unknown as { Excel?: ExcelApi }).Excel ?? null
}

/**
 * Reads the current selection's values (e.g. for "use selected cell" in lookup).
 * Returns the 2D array of cell values, or null if Excel API unavailable or read failed.
 */
export async function readSelectionValues(): Promise<(string | number)[][] | null> {
	const excel = getExcelApi()
	if (!excel?.run) return null
	let result: (string | number)[][] | null = null
	await excel.run(async (context) => {
		const range = context.workbook.getSelectedRange()
		if (range.load) range.load("values")
		await context.sync()
		result = (range.values ?? []) as (string | number)[][]
	})
	return result
}

/**
 * Reads the current selection's top-left cell value as a string (for ID lookup "use selected cell").
 * Returns null if Excel API unavailable, no selection, or empty cell.
 */
export async function readSelectedCellValue(): Promise<string | null> {
	const values = await readSelectionValues()
	const raw = values?.[0]?.[0]
	if (raw == null || raw === "") return null
	return String(raw)
}

/**
 * Writes a 2D array (header + rows) to the worksheet starting at the current selection.
 * Resizes the selection to match tableValues dimensions.
 */
export async function writeValuesToSelection(
	tableValues: (string | number)[][],
): Promise<{ success: true } | { success: false; message: string }> {
	const excel = getExcelApi()
	if (!excel?.run) {
		return { success: false, message: "Excel API not available." }
	}
	const rowCount = tableValues.length
	const colCount = tableValues[0]?.length ?? 0
	if (rowCount === 0 || colCount === 0) {
		return { success: false, message: "No data to insert." }
	}
	try {
		await excel.run(async (context) => {
			const startRange = context.workbook.getSelectedRange()
			const targetRange = startRange.getResizedRange?.(rowCount - 1, colCount - 1)
			if (!targetRange) throw new Error("getResizedRange not available")
			targetRange.values = tableValues
			await context.sync()
		})
		return { success: true }
	} catch (err) {
		const message = err instanceof Error ? err.message : String(err)
		return { success: false, message }
	}
}

/**
 * Full dependency rebuild + recalc so custom functions re-run and show the busy state while pending.
 * Prefer over `"Recalculate"` for streaming / custom-function refresh UX.
 */
const CALC_FULL_REBUILD = "FullRebuild" as const

/**
 * Forces recalculation of all formulas in the workbook, re-running AIA custom functions
 * so they fetch the latest data. Uses full rebuild so cells enter the calculating/busy state.
 */
export async function refreshWorkbookFormulas(): Promise<
	{ success: true } | { success: false; message: string }
> {
	const excel = getExcelApi()
	if (!excel?.run) return { success: false, message: "Excel API not available." }
	try {
		await excel.run(async (context) => {
			// Cast to the real Excel workbook shape — not part of the minimal mock type.
			const app = (context.workbook as unknown as {
				application?: { calculate?: (type: string) => void }
			}).application
			app?.calculate?.(CALC_FULL_REBUILD)
			await context.sync()
		})
		return { success: true }
	} catch (err) {
		return { success: false, message: err instanceof Error ? err.message : String(err) }
	}
}

/**
 * Registers a handler that forces a full workbook recalculation whenever the user
 * activates a different worksheet tab. Called once at add-in startup.
 *
 * Uses the real Excel event API directly (not the minimal mock ExcelApi wrapper)
 * because event registration isn't part of the mock surface.
 */
export function registerWorksheetActivationRefresh(): void {
	const win = typeof window !== "undefined" ? window : (globalThis as unknown as Window)
	const ExcelGlobal = (win as unknown as { Excel?: { run?: (...args: unknown[]) => unknown; CalculationType?: { recalculate?: string } } }).Excel
	if (!ExcelGlobal?.run) return

	void ExcelGlobal.run(async (context: unknown) => {
		const ctx = context as {
			workbook: {
				worksheets: {
					onActivated: { add: (handler: (event: { worksheetId: string }) => Promise<void>) => void }
				}
			}
			sync: () => Promise<void>
		}
		ctx.workbook.worksheets.onActivated.add(async (_event) => {
			// Fire-and-forget recalculation on the whole workbook when a sheet is activated.
			// This ensures AIA formulas always reflect the latest API data on first view.
			await refreshWorkbookFormulas()
		})
		await ctx.sync()
	})
}

/**
 * Writes a formula string to the active cell (current selection).
 */
export async function writeFormulaToSelection(
	formula: string,
): Promise<{ success: true } | { success: false; message: string }> {
	const excel = getExcelApi()
	if (!excel?.run) {
		return { success: false, message: "Excel API not available." }
	}
	try {
		await excel.run(async (context) => {
			const range = context.workbook.getSelectedRange()
			range.formulas = [[formula]]
			await context.sync()
		})
		return { success: true }
	} catch (err) {
		const message = err instanceof Error ? err.message : String(err)
		return { success: false, message }
	}
}
